import { NodeId, TreeView, SharedTree } from '@fluid-experimental/tree';
import {
  MfsCollection,
  MfsCollectionItem,
  MfsCollectionId,
  MfsItem,
  MfsItemId,
  MfsSubCollectionId,
  SourcePosition,
} from '../../store/mfsTypes';
import { OrderedListEditorType, OrderedListReaderType, OrderedListWriter, OrderedListReader } from './orderedList';
import { getCollectionNode, itemExists } from './nodesRead';
import { isMfsCollectionId, isMfsItem } from '../..';
import { getItem } from './itemsRead';
import { logVerbose } from '../../log';
import { Writable } from '../../utility';

export type MfsFluidCollection = OrderedListEditorType<MfsItemId>;
export type MfsFluidReadOnlyCollection = OrderedListReaderType<MfsItemId>;

export type CollectionItemInternal = {
  readonly item: MfsItem;
  readonly elementId: NodeId;
  readonly parentId?: NodeId;
  readonly children?: CollectionItemInternal[];
};

type Selectable<T> = {
  [Property in keyof T]: T[Property] extends MfsItem
    ? Partial<MfsItem>
    : T[Property] extends CollectionItemInternal[] | undefined
    ? Selectable<T>[]
    : T[Property];
};

export type SelectableCollectionItem = Selectable<CollectionItemInternal>;

export type CollectionIdInternal = {
  /** The MFS item id of the MfsCollection item. */
  collectionId: MfsCollectionId;
  /** The node id of the collection-root node. */
  collectionNodeId: NodeId;
  /** The node id of the sub-collection root node. */
  parentId?: NodeId;
};

/** Represents a collection entry. */
export type CollectionEntryInternal = {
  readonly itemId: MfsItemId;
  readonly elementId: NodeId;
  readonly parentId?: NodeId;
  readonly children?: CollectionEntryInternal[];
};

/** Gets the given item or collection if it exists, otherwise undefined. */
function tryGetItemOrCollection(
  tree: SharedTree,
  view: TreeView,
  id: MfsItemId,
  select?: string[]
): MfsItem | MfsCollection | undefined {
  return getItem<MfsItem>(tree, view, id, (i): i is MfsItem => isMfsItem(i, select), select);
}

/** @returns The internal collection identifier. */
export function getCollectionNodeId(
  tree: SharedTree,
  view: TreeView,
  collectionId: MfsCollectionId | MfsSubCollectionId
): CollectionIdInternal {
  const { id, parentId } = isMfsCollectionId(collectionId) ? { id: collectionId, parentId: undefined } : collectionId;
  const collectionNodeId = getCollectionNode(tree, view, id).identifier;
  return { collectionNodeId, collectionId: id, ...(parentId && { parentId }) };
}

/** @returns A new instance of {@link MfsFluidCollection}. */
export function getMutableCollection(tree: SharedTree, view: TreeView, collectionNodeId: NodeId): MfsFluidCollection {
  return new OrderedListWriter<MfsItemId>(tree, view, collectionNodeId);
}

/** @yields The entries under the `parentId` element at level-1. */
export function* getCollectionEntries(
  view: TreeView,
  id: CollectionIdInternal,
  isReverse = false
): IterableIterator<CollectionEntryInternal> {
  const collection = getImmutableCollection(view, id.collectionNodeId);

  for (const element of collection.getElements(id.parentId, isReverse)) {
    yield {
      itemId: element.elementValue,
      elementId: element.elementId,
      ...(element.parentId !== id.collectionNodeId && { parentId: element.parentId }), // never expose collection root node id.
    };
  }
}

/** @returns A new instance of {@link MfsFluidReadOnlyCollection}. */
function getImmutableCollection(view: TreeView, collectionNodeId: NodeId): MfsFluidReadOnlyCollection {
  return new OrderedListReader<MfsItemId>(view, collectionNodeId);
}

/** @returns The collection item (id) at given position. */
export function getCollectionElementAt(
  tree: SharedTree,
  view: TreeView,
  collectionId: MfsCollectionId,
  position: SourcePosition
): MfsItemId {
  const collectionNodeId = getCollectionNode(tree, view, collectionId).identifier;
  const collection = getImmutableCollection(view, collectionNodeId);
  return collection.get(position.elementId);
}

export function* getCollectionItemsWithoutPaging(
  tree: SharedTree,
  view: TreeView,
  id: CollectionIdInternal,
  depth: number,
  isReverse: boolean,
  select?: string[]
): IterableIterator<MfsCollectionItem> {
  for (const entry of getCollectionEntries(view, id, isReverse)) {
    const item = tryGetItemOrCollection(tree, view, entry.itemId, select);
    if (!item) {
      continue;
    }

    logVerbose(
      'getCollectionItemsWithoutPaging',
      `Getting collection item id: ${item.id}, kind:${item.kind ?? 'single item'}, collectionId: ${id.collectionId}${
        id.parentId ? ',parentId:' : ''
      } ${id.parentId ? id.parentId : ''}`
    );

    const result: Writable<MfsCollectionItem> = {
      item: item,
      elementId: entry.elementId,
      ...(id.parentId && { parentId: entry.parentId }),
    };

    // get children
    if (depth > 1) {
      const children: MfsCollectionItem[] = [];

      // never iterate into the nested collections
      const childEntries =
        item.kind === undefined
          ? getCollectionItemsWithoutPaging(
              tree,
              view,
              { collectionNodeId: id.collectionNodeId, parentId: entry.elementId, collectionId: id.collectionId },
              depth - 1,
              isReverse,
              select
            )
          : [];

      for (const childEntry of childEntries) {
        children.push(childEntry);
      }

      if (children.length > 0) {
        result.children = children;
      }
    }

    yield result;
  }
}

/** @yields Collection entries (matching with the given itemId, if specified), up to the given depth. */
export function* getEntriesOf(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId | undefined,
  id: CollectionIdInternal,
  depth = Number.MAX_SAFE_INTEGER
): IterableIterator<CollectionEntryInternal> {
  if (itemId && !itemExists(tree, view, itemId)) {
    logVerbose('getEntriesOf', `itemId: ${itemId} does not exist.`);
    return;
  }

  function shouldYield(e: CollectionEntryInternal): boolean {
    return (!itemId && itemExists(tree, view, e.itemId)) || itemId === e.itemId;
  }

  for (const entry of getCollectionEntries(view, id)) {
    if (shouldYield(entry)) {
      yield entry;
    }

    // get children
    if (depth > 1) {
      for (const childEntry of getEntriesOf(tree, view, itemId, { ...id, parentId: entry.elementId }, depth - 1)) {
        if (shouldYield(childEntry)) {
          yield childEntry;
        }
      }
    }
  }
}

/** Checks if the given item id is an element of the collection up to the given depth. */
export function hasElement(
  tree: SharedTree,
  view: TreeView,
  itemId: MfsItemId,
  id: CollectionIdInternal,
  depth = Number.MAX_SAFE_INTEGER
): boolean {
  const found = getEntriesOf(tree, view, itemId, id, depth).next();
  logVerbose('hasElement', `hasElement itemId: ${itemId}, depth: ${depth}, first entry:${found.value}`);
  return found.value !== undefined;
}
